import { useEffect, useRef, useState } from "react";
import { Terminal, Copy } from "lucide-react";

const SCRIPTS = [
  {
    prompt: "Build a React button with a neon hover effect",
    file: "Button.jsx",
    code: `export const Neon = () => (
  <button className="neon-btn">
    Launch 🚀
  </button>
);`,
  },
  {
    prompt: "Reverse a linked list in Python",
    file: "solution.py",
    code: `def reverse(head):
    prev = None
    while head:
        head.next, prev, head = prev, head, head.next
    return prev`,
  },
  {
    prompt: "FastAPI login endpoint with JWT",
    file: "auth.py",
    code: `@app.post("/login")
async def login(body: LoginIn):
    user = await verify(body)
    return {"token": make_jwt(user)}`,
  },
];

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

export const HeroDemo = () => {
  const [prompt, setPrompt] = useState("");
  const [code, setCode] = useState("");
  const [file, setFile] = useState(SCRIPTS[0].file);
  const [phase, setPhase] = useState("typing"); // typing | thinking | streaming | done
  const cancelled = useRef(false);

  useEffect(() => {
    cancelled.current = false;
    async function run() {
      while (!cancelled.current) {
        for (const s of SCRIPTS) {
          if (cancelled.current) return;
          setFile(s.file);
          setCode("");
          setPrompt("");
          setPhase("typing");
          for (let i = 1; i <= s.prompt.length; i++) {
            if (cancelled.current) return;
            setPrompt(s.prompt.slice(0, i));
            await sleep(26);
          }
          await sleep(450);
          setPhase("thinking");
          await sleep(750);
          setPhase("streaming");
          for (let i = 1; i <= s.code.length; i += 2) {
            if (cancelled.current) return;
            setCode(s.code.slice(0, i));
            await sleep(14);
          }
          setCode(s.code);
          setPhase("done");
          await sleep(2600);
        }
      }
    }
    run();
    return () => {
      cancelled.current = true;
    };
  }, []);

  return (
    <div className="relative animate-float" data-testid="hero-demo">
      <div
        className="absolute -inset-6 rounded-3xl blur-3xl opacity-30"
        style={{ background: "radial-gradient(circle, #00FF66 0%, transparent 70%)" }}
      />
      <div className="relative rounded-2xl border border-white/10 bg-[#0D0D0E] overflow-hidden shadow-2xl nig-glow-border">
        <div className="flex items-center gap-2 px-4 py-3 border-b border-white/10 bg-[#141415]">
          <div className="flex gap-1.5">
            <span className="w-3 h-3 rounded-full bg-[#FF5F56]" />
            <span className="w-3 h-3 rounded-full bg-[#FFBD2E]" />
            <span className="w-3 h-3 rounded-full bg-[#27C93F]" />
          </div>
          <span className="ml-2 text-xs font-mono text-zinc-500">nigamai — live demo</span>
          <span className="ml-auto flex items-center gap-1.5 text-[10px] text-[#00FF66]">
            <span className="w-1.5 h-1.5 rounded-full bg-[#00FF66] animate-glow" /> LIVE
          </span>
        </div>

        <div className="p-5 min-h-[280px] flex flex-col gap-4 text-sm">
          {/* user prompt */}
          <div className="flex justify-end">
            <div className="bg-[#1F1F22] rounded-2xl rounded-tr-sm px-4 py-2.5 text-zinc-200 max-w-[85%] min-h-[42px]">
              {prompt}
              {phase === "typing" && <span className="inline-block w-1.5 h-4 bg-[#00FF66] ml-0.5 cursor-blink align-middle" />}
            </div>
          </div>

          {/* assistant */}
          {phase !== "typing" && (
            <div className="flex gap-3">
              <div className="w-7 h-7 rounded-lg bg-[#00FF66] flex items-center justify-center shrink-0">
                <Terminal className="w-4 h-4 text-black" />
              </div>
              <div className="flex-1 min-w-0">
                {phase === "thinking" ? (
                  <div className="flex items-center gap-1.5 pt-2">
                    <span className="w-2 h-2 rounded-full bg-zinc-600 animate-bounce" style={{ animationDelay: "0ms" }} />
                    <span className="w-2 h-2 rounded-full bg-zinc-600 animate-bounce" style={{ animationDelay: "150ms" }} />
                    <span className="w-2 h-2 rounded-full bg-zinc-600 animate-bounce" style={{ animationDelay: "300ms" }} />
                  </div>
                ) : (
                  <div className="rounded-xl border border-white/10 bg-black overflow-hidden">
                    <div className="flex items-center justify-between px-3 py-2 border-b border-white/10 bg-[#141415]">
                      <span className="text-[11px] font-mono text-zinc-500">{file}</span>
                      <Copy className="w-3.5 h-3.5 text-zinc-500" />
                    </div>
                    <pre className="p-3 text-[12px] leading-relaxed font-mono text-[#8CE99A] overflow-x-auto whitespace-pre-wrap min-h-[92px]">
{code}
                      {phase === "streaming" && <span className="inline-block w-1.5 h-3.5 bg-[#00FF66] ml-0.5 cursor-blink align-middle" />}
                    </pre>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="absolute -left-4 top-1/3 hidden sm:flex items-center gap-2 px-3 py-2 rounded-xl nig-glass border border-white/10 text-xs animate-float" style={{ animationDelay: "1s" }}>
        <span className="w-1.5 h-1.5 rounded-full bg-[#00FF66] animate-glow" /> Streaming live
      </div>
      <div className="absolute -right-4 bottom-8 hidden sm:flex items-center gap-2 px-3 py-2 rounded-xl nig-glass border border-white/10 text-xs animate-float" style={{ animationDelay: "2s" }}>
        ⌘ Copy & ship
      </div>
    </div>
  );
};
